import { Box, Button, Collapse, IconButton, Paper, TextField, Typography } from '@mui/material'
import { Edit as EditIcon, ExpandMore } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import { colors, borderRadius, sxPresets } from '@/theme'
import type { PatientDetails, UpdatePatientBody } from '@/services/patients'
import { useUnsavedChangesProcessing } from '@/hooks/useUnsavedChangesProcessing'

interface MedicalHistoryProps {
  patient: PatientDetails
  onSave?: (body: UpdatePatientBody) => Promise<void>
  isSaving?: boolean
  readOnly?: boolean
}

export default function MedicalHistory({ patient, onSave, isSaving, readOnly }: MedicalHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)

  const [otherObservations, setOtherObservations] = useState(patient.otherObservations || '')
  useEffect(() => {
    setOtherObservations(patient.otherObservations || '')
  }, [patient])

  const isFieldDirty = otherObservations !== (patient.otherObservations || '')
  useUnsavedChangesProcessing(isEditMode && isFieldDirty)

  const handleEdit = () => {
    setOtherObservations(patient.otherObservations || '')
    setIsEditMode(true)
  }

  const handleCancel = () => {
    setOtherObservations(patient.otherObservations || '')
    setIsEditMode(false)
  }

  const handleSave = async () => {
    if (readOnly) return
    await onSave?.({ other: otherObservations })
    setIsEditMode(false)
  }

  return (
    <Paper id='medical-history' elevation={1} sx={{ border: `1px solid ${colors.border.default}` }}>
      {/* Header */}
      <Box sx={{ ...sxPresets().section.header }}>
        <Box sx={{ cursor: 'pointer', flex: 1 }} onClick={() => setIsExpanded(!isExpanded)}>
          <Typography variant='sectionTitle'>Medical History</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isExpanded && !isEditMode && !readOnly && (
            <IconButton onClick={handleEdit} size='small' color='primary'>
              <EditIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          )}
          {isExpanded && isEditMode && (
            <>
              <Button variant='primary' onClick={handleSave} disabled={!isFieldDirty || isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant='ghost' onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            </>
          )}
          <IconButton
            size='small'
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              ...sxPresets().expandButton,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </Box>

      {/* Expandable Content */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            p: 3,
            bgcolor: colors.background.surface,
            ...sxPresets().grid.twoColumn,
            borderRadius: `0 0 ${borderRadius.lg} ${borderRadius.lg}`
          }}
        >
          <Box>
            <Typography variant='body1' sx={{ mb: 1 }}>
              Medications
            </Typography>
            <Typography variant='cardTitle' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {patient.medications || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body1' sx={{ mb: 1 }}>
              Allergies
            </Typography>
            <Typography variant='cardTitle' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {patient.allergies || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body1' sx={{ mb: 1 }}>
              Family History
            </Typography>
            <Typography variant='cardTitle' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {patient.familyHistory || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body1' sx={{ mb: 1 }}>
              Social History
            </Typography>
            <Typography variant='cardTitle' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {patient.socialHistory || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body1' sx={{ mb: 1 }}>
              Conditions
            </Typography>
            <Typography variant='cardTitle' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {patient.conditions || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body1' sx={{ mb: 1 }}>
              Other Observations
            </Typography>
            {isEditMode ? (
              <TextField
                value={otherObservations}
                onChange={e => setOtherObservations(e.target.value)}
                fullWidth
                size='small'
                multiline
                minRows={2}
                inputProps={{ maxLength: 255 }}
              />
            ) : (
              <Typography variant='cardTitle' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                {patient.otherObservations || '-'}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  )
}
